/**
 * Checks if a given address string matches PO Box patterns.
 * This regex matches various formats of PO Box addresses including:
 * - P.O. Box, PO Box, Post Office Box
 * - Mail Box, Mailing Box
 * - Box, Bin variations
 *
 * @param {string} value - The address string to check
 * @returns {boolean} True if the address matches a PO Box pattern, false otherwise
 */
export function isPOBox(value) {
  const poBoxPattern =
    /\b(P\.?\s*O\.?\s*B(ox|x)|Post\s+Office\s+Box|POB|Mail\s+Box|Box)\b[\s#.]*(?:[A-Za-z]+\s+)?\d+/i;

  if (value && poBoxPattern.test(value)) {
    return true;
  }
  return false;
}

/**
 * Validates address dropdown fields that PcDropdownReskinned does not include
 * in v-form.validate(). Checks that State/Region and Country have values.
 *
 * @param {Object} formData - The address form data object
 * @param {boolean} isUS - Whether the country is US (determines state vs region check)
 * @returns {boolean} True if there are dropdown validation errors, false if valid
 */
export function validateAddressDropdowns(formData, isUS) {
  var hasErrors = false;
  if (isUS) {
    if (!formData.stateCode) hasErrors = true;
  } else {
    if (!formData.otherRegion) hasErrors = true;
  }
  if (!formData.countryCode) hasErrors = true;
  return hasErrors;
}

/**
 * Builds an address payload object for API submission from form data.
 * Sets stateCode or otherRegion based on whether the country is US.
 *
 * @param {Object} formData - The address form data object
 * @param {boolean} isUS - Whether the country is US (determines state vs region)
 * @returns {Object} The address payload ready for API submission
 */
export function buildAddressPayload(formData, isUS) {
  return {
    addressLine1: formData.addressLine1,
    addressLine2: formData.addressLine2 || null,
    isPoBox: formData.isPoBox || false,
    city: formData.city,
    stateCode: isUS ? formData.stateCode : null,
    otherRegion: isUS ? null : formData.otherRegion,
    postalCode: formData.postalCode,
    countryCode: formData.countryCode,
  };
}

/**
 * Builds an address payload object for API submission from form data with default values.
 *
 * @param {Object} formData - The address form data object
 * @param {boolean} isUS - Whether the country is US (determines state vs region)
 * @returns {Object} The address payload ready for API submission with default values
 */
export function buildAddressPayloadWithDefaults(formData, isUS) {
  return {
    addressLine1: formData.addressLine1 || "",
    addressLine2: formData.addressLine2 || null,
    isPoBox: formData.isPoBox || false,
    city: formData.city || "",
    stateCode: isUS ? formData.stateCode || "" : null,
    otherRegion: isUS ? null : formData.otherRegion || "",
    postalCode: formData.postalCode || "",
    countryCode: formData.countryCode || "US",
  };
}
